import { createContext, useContext, useState, useEffect } from 'react';
import { businessService } from '@/api/services/businessService';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';

const BusinessContext = createContext();

const STORAGE_KEY = 'fg_active_business_id';

export const BusinessProvider = ({ children }) => {
  const { user } = useAuth();

  const [activeId, setActiveId] = useState(() => {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? parseInt(s, 10) : null;
  });

  // size: 500 (the backend max) so the switcher resolves businesses added long ago.
  // Matters for the admin account that onboards most listings; regular owners have
  // a handful. Keeps this list in sync with the MyBusinesses page (also size: 500),
  // so "Switch to this" from there always resolves here.
  const { data: businesses = [] } = useQuery({
    queryKey: ['owner-businesses-ctx', user?.email],
    queryFn: () => businessService.getMy({ size: 500 }),
    enabled: !!user?.email && ['BUSINESS_OWNER', 'ADMIN', 'SUPER_ADMIN'].includes(user?.role),
    staleTime: 2 * 60 * 1000,
  });

  // Auto-select the first business ONLY when nothing has been chosen yet (fresh
  // login / cleared storage). Never overwrite a deliberate selection just because
  // it isn't in the loaded page — that's what previously made "Switch to this"
  // snap back to the newest business for an out-of-window listing.
  useEffect(() => {
    if (businesses.length === 0 || activeId != null) return;
    const first = businesses[0];
    setActiveId(first.id);
    localStorage.setItem(STORAGE_KEY, String(first.id));
  }, [businesses, activeId]);

  const setActiveBusiness = (biz) => {
    setActiveId(biz.id);
    localStorage.setItem(STORAGE_KEY, String(biz.id));
  };

  const activeBusiness = businesses.find(b => b.id === activeId) ?? businesses[0] ?? null;

  return (
    <BusinessContext.Provider value={{ activeBusiness, businesses, setActiveBusiness }}>
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusiness = () => {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used within BusinessProvider');
  return ctx;
};
